// src/pages/courier/DocumentManagerModal.jsx
import React, { useState } from 'react';
import {
  X,
  FileText,
  Upload,
  Eye,
  Trash2,
  FileCode,
  Plus,
  RefreshCw,
  Download
} from 'lucide-react';

const COURIER_DOC_TYPES = [
  'Business Registration Certificate',
  'GST Certificate',
  'PAN / Business Tax Document',
  'Authorization / Master Agreement Document',
  'Other Business Document'
];

const BRANCH_DOC_TYPES = [
  'Branch Authorization Letter',
  'Branch Registration / Establishment Document',
  'Branch Address Proof',
  'Courier-Issued Authorization Document',
  'Other Branch Document'
];

const DocumentManagerModal = ({
  isOpen,
  onClose,
  entityType = 'COURIER', // 'COURIER' | 'BRANCH'
  entityTitle = '',
  documents = [],
  onSaveDocuments
}) => {
  const isBranch = entityType === 'BRANCH';
  const docTypeOptions = isBranch ? BRANCH_DOC_TYPES : COURIER_DOC_TYPES;

  const [docList, setDocList] = useState(documents || []);
  const [isAdding, setIsAdding] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);

  // New doc form state
  const [docForm, setDocForm] = useState({
    documentName: '',
    documentType: docTypeOptions[0],
    documentNumber: '',
    issueDate: '',
    expiryDate: '',
    notes: '',
    fileName: '',
    fileUrl: '',
    fileSize: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const sizeStr = `${(file.size / 1024).toFixed(1)} KB`;
      const reader = new FileReader();
      reader.onload = () => {
        setDocForm((prev) => ({
          ...prev,
          fileName: file.name,
          fileUrl: reader.result,
          fileSize: sizeStr,
          documentName: prev.documentName || file.name.split('.')[0]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setDocForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const resetForm = () => {
    setDocForm({
      documentName: '',
      documentType: docTypeOptions[0],
      documentNumber: '',
      issueDate: '',
      expiryDate: '',
      notes: '',
      fileName: '',
      fileUrl: '',
      fileSize: ''
    });
    setFormErrors({});
    setIsAdding(false);
    setReplacingIndex(null);
  };

  const validateDocForm = () => {
    const errs = {};
    if (!docForm.documentName.trim()) errs.documentName = 'Document name is required';
    if (!docForm.documentType) errs.documentType = 'Document type is required';
    if (!docForm.fileUrl && !docForm.fileName) errs.file = 'File is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddOrUpdateDoc = async (e) => {
    e.preventDefault();
    if (!validateDocForm()) return;

    const newDocObj = {
      documentId: `DOC-${Date.now()}`,
      documentName: docForm.documentName.trim(),
      documentType: docForm.documentType,
      documentNumber: docForm.documentNumber.trim(),
      issueDate: docForm.issueDate,
      expiryDate: docForm.expiryDate,
      notes: docForm.notes.trim(),
      fileName: docForm.fileName || `${docForm.documentName}.pdf`,
      fileUrl: docForm.fileUrl || '',
      fileSize: docForm.fileSize || 'Standard',
      uploadedAt: new Date().toISOString(),
      uploadedBy: 'SUPERADMIN'
    };

    let updatedList = [];
    if (replacingIndex !== null) {
      updatedList = [...docList];
      updatedList[replacingIndex] = {
        ...newDocObj,
        documentId: docList[replacingIndex].documentId || newDocObj.documentId
      };
    } else {
      updatedList = [newDocObj, ...docList];
    }

    setDocList(updatedList);
    resetForm();

    // Persist to parent/database
    setSaving(true);
    try {
      await onSaveDocuments(updatedList);
    } catch (err) {
      console.error('Error saving document list:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDoc = async (index) => {
    if (!window.confirm('Are you sure you want to delete this master document?')) return;
    const updated = docList.filter((_, i) => i !== index);
    setDocList(updated);
    try {
      await onSaveDocuments(updated);
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const startReplace = (docItem, index) => {
    setReplacingIndex(index);
    setDocForm({
      documentName: docItem.documentName || '',
      documentType: docItem.documentType || docTypeOptions[0],
      documentNumber: docItem.documentNumber || '',
      issueDate: docItem.issueDate || '',
      expiryDate: docItem.expiryDate || '',
      notes: docItem.notes || '',
      fileName: docItem.fileName || '',
      fileUrl: docItem.fileUrl || '',
      fileSize: docItem.fileSize || ''
    });
    setIsAdding(true);
  };

  return (
    <div className="cm-modal-overlay">
      <div className="cm-modal-container cm-modal-lg">
        <div className="cm-modal-header">
          <div className="cm-modal-title-group">
            <div className="cm-modal-icon-badge">
              <FileText size={22} color="#8a6b32" />
            </div>
            <div>
              <h3 className="cm-modal-title">
                {isBranch ? 'Branch Master Documents' : 'Courier Business Documents'}
              </h3>
              <p className="cm-modal-subtitle">
                {entityTitle} &bull; Stored as internal master data (No verification workflow)
              </p>
            </div>
          </div>
          <button className="cm-btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="cm-modal-body">
          {/* Header Action: Add new document button */}
          {!isAdding && (
            <div className="cm-doc-header-action">
              <span className="cm-doc-count-badge">
                <strong>{docList.length}</strong> Document{docList.length === 1 ? '' : 's'} on record
              </span>
              <button
                type="button"
                className="cm-btn-primary cm-btn-sm"
                onClick={() => {
                  resetForm();
                  setIsAdding(true);
                }}
              >
                <Plus size={15} /> Upload Document
              </button>
            </div>
          )}

          {/* Document Upload / Edit Form */}
          {isAdding && (
            <form onSubmit={handleAddOrUpdateDoc} className="cm-doc-form-card">
              <div className="cm-doc-form-header">
                <h4 className="cm-doc-form-title">
                  {replacingIndex !== null ? 'Replace Document' : 'Upload New Document'}
                </h4>
                <button type="button" className="cm-btn-link" onClick={resetForm}>
                  Cancel
                </button>
              </div>

              <div className="cm-form-grid">
                <div className="cm-form-group">
                  <label className="cm-label">
                    Document Name <span className="cm-req">*</span>
                  </label>
                  <input
                    type="text"
                    name="documentName"
                    value={docForm.documentName}
                    onChange={handleFormChange}
                    placeholder="e.g. GST Registration Certificate 2026"
                    className={`cm-input ${formErrors.documentName ? 'cm-input-error' : ''}`}
                  />
                  {formErrors.documentName && (
                    <span className="cm-error-msg">{formErrors.documentName}</span>
                  )}
                </div>

                <div className="cm-form-group">
                  <label className="cm-label">
                    Document Type <span className="cm-req">*</span>
                  </label>
                  <select
                    name="documentType"
                    value={docForm.documentType}
                    onChange={handleFormChange}
                    className="cm-select"
                  >
                    {docTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cm-form-group">
                  <label className="cm-label">Document Number (if applicable)</label>
                  <input
                    type="text"
                    name="documentNumber"
                    value={docForm.documentNumber}
                    onChange={handleFormChange}
                    placeholder="e.g. 21AABCC1234F1Z5 or REG-98765"
                    className="cm-input"
                  />
                </div>

                <div className="cm-form-group">
                  <label className="cm-label">
                    Select File <span className="cm-req">*</span>
                  </label>
                  <div className="cm-file-dropzone">
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={handleFileChange}
                      className="cm-file-input"
                      id="doc-file-input"
                    />
                    <label htmlFor="doc-file-input" className="cm-file-dropzone-inner">
                      <Upload size={20} color="#8a6b32" />
                      <span>{docForm.fileName ? docForm.fileName : 'Choose PDF, PNG, or JPG file'}</span>
                      {docForm.fileSize && <small>({docForm.fileSize})</small>}
                    </label>
                  </div>
                  {formErrors.file && <span className="cm-error-msg">{formErrors.file}</span>}
                </div>

                <div className="cm-form-group">
                  <label className="cm-label">Issue Date</label>
                  <input
                    type="date"
                    name="issueDate"
                    value={docForm.issueDate}
                    onChange={handleFormChange}
                    className="cm-input"
                  />
                </div>

                <div className="cm-form-group">
                  <label className="cm-label">Expiry Date</label>
                  <input
                    type="date"
                    name="expiryDate"
                    value={docForm.expiryDate}
                    onChange={handleFormChange}
                    className="cm-input"
                  />
                </div>

                <div className="cm-form-group cm-col-full">
                  <label className="cm-label">Notes / Remarks</label>
                  <input
                    type="text"
                    name="notes"
                    value={docForm.notes}
                    onChange={handleFormChange}
                    placeholder="e.g. Validated directly with courier regional head"
                    className="cm-input"
                  />
                </div>
              </div>

              <div className="cm-doc-form-footer">
                <button type="button" className="cm-btn-secondary cm-btn-sm" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="cm-btn-primary cm-btn-sm" disabled={saving}>
                  {replacingIndex !== null ? 'Save Replacement' : 'Save Document'}
                </button>
              </div>
            </form>
          )}

          {/* List of existing documents */}
          <div className="cm-doc-list">
            {docList.length === 0 ? (
              <div className="cm-doc-empty">
                <FileText size={40} color="#cbd5e1" />
                <p>No documents uploaded yet for this master record.</p>
                <p className="cm-text-muted">
                  SuperAdmin can upload registration certificates, authorization deeds, or branch letters.
                </p>
              </div>
            ) : (
              docList.map((docItem, idx) => (
                <div key={docItem.documentId || idx} className="cm-doc-item">
                  <div className="cm-doc-icon-col">
                    <FileCode size={28} color="#8a6b32" />
                  </div>
                  <div className="cm-doc-info-col">
                    <div className="cm-doc-name-row">
                      <strong className="cm-doc-name">{docItem.documentName}</strong>
                      <span className="cm-doc-type-pill">{docItem.documentType}</span>
                    </div>
                    <div className="cm-doc-meta-row">
                      {docItem.documentNumber && (
                        <span>
                          <strong>No:</strong> {docItem.documentNumber}
                        </span>
                      )}
                      {docItem.issueDate && (
                        <span>
                          <strong>Issued:</strong> {docItem.issueDate}
                        </span>
                      )}
                      {docItem.expiryDate && (
                        <span>
                          <strong>Expires:</strong> {docItem.expiryDate}
                        </span>
                      )}
                      {docItem.fileName && (
                        <span className="cm-doc-filename">
                          File: {docItem.fileName} {docItem.fileSize ? `(${docItem.fileSize})` : ''}
                        </span>
                      )}
                    </div>
                    {docItem.notes && <p className="cm-doc-notes">"{docItem.notes}"</p>}
                  </div>
                  <div className="cm-doc-actions-col">
                    {docItem.fileUrl && (
                      <button
                        type="button"
                        className="cm-btn-icon"
                        title="View Document"
                        onClick={() => setViewingDoc(docItem)}
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="cm-btn-icon"
                      title="Replace Document"
                      onClick={() => startReplace(docItem, idx)}
                    >
                      <RefreshCw size={15} />
                    </button>
                    <button
                      type="button"
                      className="cm-btn-icon cm-btn-icon-danger"
                      title="Delete Document"
                      onClick={() => handleDeleteDoc(idx)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal for viewing document preview */}
        {viewingDoc && (
          <div className="cm-modal-overlay" style={{ zIndex: 1100 }}>
            <div className="cm-modal-container cm-modal-md">
              <div className="cm-modal-header">
                <h4 className="cm-modal-title">{viewingDoc.documentName}</h4>
                <button className="cm-btn-close" onClick={() => setViewingDoc(null)}>
                  <X size={18} />
                </button>
              </div>
              <div className="cm-modal-body" style={{ textAlign: 'center', padding: '20px' }}>
                {viewingDoc.fileUrl && viewingDoc.fileUrl.startsWith('data:image') ? (
                  <img
                    src={viewingDoc.fileUrl}
                    alt={viewingDoc.documentName}
                    style={{ maxWidth: '100%', maxHeight: '450px', borderRadius: '8px' }}
                  />
                ) : (
                  <div style={{ padding: '30px', background: '#f8fafc', borderRadius: '8px' }}>
                    <FileText size={48} color="#8a6b32" style={{ margin: '0 auto 12px' }} />
                    <p>
                      <strong>{viewingDoc.fileName || viewingDoc.documentName}</strong>
                    </p>
                    <p style={{ color: '#64748b', fontSize: '13px' }}>
                      Document stored as SuperAdmin master data.
                    </p>
                    {viewingDoc.fileUrl && (
                      <a
                        href={viewingDoc.fileUrl}
                        download={viewingDoc.fileName || 'document.pdf'}
                        className="cm-btn-primary cm-btn-sm"
                        style={{ display: 'inline-flex', marginTop: '16px', gap: '6px' }}
                      >
                        <Download size={15} /> Download Document
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="cm-modal-footer">
          <button type="button" className="cm-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentManagerModal;
